/**
 * dev-dashboard.ts — #devs channel renderer (channel_kind='dev_dashboard').
 *
 * Vercel-deployments-style card grid showing live status of every hub-dev*
 * + hub-watcher container. Polls /api/devs/status every 5s. Snapshot is
 * written by hub-watcher each cycle (POST /api/devs/snapshot).
 *
 * Mitchell ask 2026-04-27: "whats devs are running? can you give me a
 * ui / channel for this? not as text but as css cards?"
 */

import { get } from '../api';

interface DevStatusEntry {
  name: string;
  container_status: string;
  claude_alive: boolean;
  current_brief: string | null;
  last_activity_iso: string | null;
  idle_minutes: number | null;
  done_flag: string | null;
  badge: string;
  cost_today_usd?: number | null;
  tokens_today?: number | null;
}

interface DevStatusSnapshot {
  written_at: string;
  written_by: string;
  devs: DevStatusEntry[];
  aggregate_cost_today_usd?: number | null;
  aggregate_tokens_today?: number | null;
}

const POLL_INTERVAL_MS = 5000;

let pollTimer: ReturnType<typeof setInterval> | null = null;
let containerEl: HTMLElement | null = null;

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'never';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function badgeClass(badge: string): string {
  // Vercel-style: green=running, amber=stalled, grey=idle, blue=done, red=error
  switch (badge) {
    case 'running':
      return 'dev-card-badge dev-card-badge-running';
    case 'stalled':
      return 'dev-card-badge dev-card-badge-stalled';
    case 'done':
      return 'dev-card-badge dev-card-badge-done';
    case 'error':
      return 'dev-card-badge dev-card-badge-error';
    case 'idle':
    default:
      return 'dev-card-badge dev-card-badge-idle';
  }
}

function fmtCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return '—';
  return `$${usd.toFixed(2)}`;
}

function fmtTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function cardHtml(d: DevStatusEntry): string {
  const briefShort =
    d.current_brief && d.current_brief.length > 60
      ? d.current_brief.slice(0, 57) + '...'
      : d.current_brief || '';
  const idleStr =
    d.idle_minutes !== null && d.idle_minutes !== undefined
      ? `${d.idle_minutes}m`
      : '—';
  const lastActivity = d.last_activity_iso
    ? relativeTime(d.last_activity_iso)
    : 'never';
  const spendRow =
    d.cost_today_usd !== null && d.cost_today_usd !== undefined
      ? `<div class="dev-card-row">
          <span class="dev-card-label">spend today</span>
          <span class="dev-card-value">${esc(fmtCost(d.cost_today_usd))} / ${esc(fmtTokens(d.tokens_today))}</span>
        </div>`
      : '';
  return `
    <div class="dev-card dev-card-${esc(d.badge)}">
      <div class="dev-card-header">
        <div class="dev-card-name">${esc(d.name)}</div>
        <div class="${badgeClass(d.badge)}">${esc(d.badge)}</div>
      </div>
      <div class="dev-card-row">
        <span class="dev-card-label">container</span>
        <span class="dev-card-value">${esc(d.container_status)}</span>
      </div>
      <div class="dev-card-row">
        <span class="dev-card-label">claude</span>
        <span class="dev-card-value">${d.claude_alive ? 'alive' : '—'}</span>
      </div>
      <div class="dev-card-row">
        <span class="dev-card-label">brief</span>
        <span class="dev-card-value" title="${esc(d.current_brief || '')}">${esc(briefShort) || '—'}</span>
      </div>
      <div class="dev-card-row">
        <span class="dev-card-label">last activity</span>
        <span class="dev-card-value">${esc(lastActivity)} (${esc(idleStr)})</span>
      </div>
      ${spendRow}
      ${
        d.done_flag
          ? `<div class="dev-card-flag">DONE: ${esc(d.done_flag)}</div>`
          : ''
      }
    </div>
  `;
}

function emptyHtml(): string {
  return `
    <div class="dev-empty">
      <div class="dev-empty-title">No dev status snapshot yet</div>
      <div class="dev-empty-body">
        The hub-watcher writes to <code>/api/devs/snapshot</code> on each cycle
        (~150 s). Either it hasn't completed a cycle yet, or its
        <code>devs-snapshot</code> step hasn't been wired in. Check
        <code>cc-memory/hub-watcher.log</code> on the watcher container.
      </div>
    </div>
  `;
}

function renderSnapshot(snap: DevStatusSnapshot): void {
  if (!containerEl) return;
  if (!snap.devs.length) {
    containerEl.innerHTML = emptyHtml();
    return;
  }
  const cards = snap.devs.map(cardHtml).join('');
  const meta = `Updated ${esc(relativeTime(snap.written_at))} by ${esc(snap.written_by || '—')}`;

  // Aggregate spend banner (ccusage). Watcher precomputes; FE falls back to
  // summing per-dev fields if absent so older snapshots still show a number.
  const aggCost =
    snap.aggregate_cost_today_usd ??
    snap.devs.reduce((acc, d) => acc + (d.cost_today_usd ?? 0), 0);
  const aggTokens =
    snap.aggregate_tokens_today ??
    snap.devs.reduce((acc, d) => acc + (d.tokens_today ?? 0), 0);
  const devsWithSpend = snap.devs.filter(
    (d) => d.cost_today_usd !== null && d.cost_today_usd !== undefined,
  ).length;
  const spendBanner =
    devsWithSpend > 0
      ? `<div class="dev-dashboard-spend">
          <span class="dev-dashboard-spend-label">today (ccusage):</span>
          <span class="dev-dashboard-spend-value">${esc(fmtCost(aggCost))}</span>
          <span class="dev-dashboard-spend-sep">·</span>
          <span class="dev-dashboard-spend-value">${esc(fmtTokens(aggTokens))} tokens</span>
          <span class="dev-dashboard-spend-sep">·</span>
          <span class="dev-dashboard-spend-detail">${devsWithSpend} dev${devsWithSpend === 1 ? '' : 's'} reporting</span>
        </div>`
      : '';

  containerEl.innerHTML = `
    ${spendBanner}
    <div class="dev-dashboard-meta">${meta}</div>
    <div class="dev-dashboard-grid">${cards}</div>
  `;
}

async function fetchAndRender(): Promise<void> {
  try {
    const snap = await get<DevStatusSnapshot>('/api/devs/status');
    if (snap) renderSnapshot(snap);
  } catch {
    // Silent — keep last state, retry next tick
  }
}

export function renderDevDashboard(el: HTMLElement): void {
  containerEl = el;
  el.classList.add('dev-dashboard-body');
  el.innerHTML = `
    <div class="dev-dashboard-meta">Loading dev status…</div>
    <div class="dev-dashboard-grid"></div>
  `;
  void fetchAndRender();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(() => void fetchAndRender(), POLL_INTERVAL_MS);
}

export function cleanupDevDashboard(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  containerEl = null;
}
